import React, { FC } from 'react';
import styled from 'styled-components';
import { PageBlocksListSkeleton } from 'src/components/widgets/pageBlocksList/pageBlocksList';
import {
    SkeletonBlock,
    SkeletonFooterBar,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonRow,
    SkeletonStack,
    SkeletonTable,
    SkeletonTableRow
} from 'src/components/widgets/skeleton';

const SettingsOptionalColumn = styled.div`
    @media (max-width: 760px) {
        display: none;
    }
`;

const SettingsSectionSkeleton = styled.div`
    display: grid;
    grid-template-columns: minmax(220px, 320px) minmax(0, 1fr);
    gap: 32px;
    padding: 28px 0;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    min-width: 0;

    @media (max-width: 900px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const SettingsFieldsSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    min-width: 0;

    @media (max-width: 760px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

export const SettingsTablePageSkeleton: FC = () => (
    <SkeletonPageShell aria-hidden='true'>
        <SkeletonPageHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(280px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(460px, 100%)' $height='13px' />
            </SkeletonStack>
            <SkeletonBlock $width='92px' $height='36px' $radius='6px' />
        </SkeletonPageHeader>
        <SkeletonTable>
            {Array.from({ length: 8 }).map((_, index) => (
                <SkeletonTableRow key={index} $columns='minmax(180px, 1fr) 180px 84px' $mobileColumns='minmax(0, 1fr) 84px' $mobileBreakpoint='760px'>
                    <SkeletonBlock $width={index % 2 === 0 ? '240px' : '170px'} $height='15px' />
                    <SettingsOptionalColumn>
                        <SkeletonBlock $width='100px' $height='22px' $radius='11px' />
                    </SettingsOptionalColumn>
                    <SkeletonRow $gap='8px' $justify='flex-end'>
                        <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                        <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                    </SkeletonRow>
                </SkeletonTableRow>
            ))}
        </SkeletonTable>
    </SkeletonPageShell>
);

export const SettingsFormPageSkeleton: FC = () => (
    <SkeletonPageShell aria-hidden='true'>
        <SkeletonPageHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(280px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(460px, 100%)' $height='13px' />
            </SkeletonStack>
        </SkeletonPageHeader>
        {Array.from({ length: 3 }).map((_, index) => (
            <SettingsSectionSkeleton key={index}>
                <SkeletonStack $gap='8px'>
                    <SkeletonBlock $width={index % 2 === 0 ? '190px' : '150px'} $height='20px' />
                    <SkeletonBlock $width='min(280px, 100%)' $height='12px' />
                    <SkeletonBlock $width='min(220px, 100%)' $height='12px' />
                </SkeletonStack>
                <SettingsFieldsSkeleton>
                    <SkeletonBlock $height='40px' $radius='6px' />
                    <SkeletonBlock $height='40px' $radius='6px' />
                    <SkeletonBlock $height='40px' $radius='6px' />
                    <SkeletonBlock $height='40px' $radius='6px' />
                </SettingsFieldsSkeleton>
            </SettingsSectionSkeleton>
        ))}
        <SkeletonFooterBar>
            <SkeletonBlock $width='110px' $height='36px' $radius='6px' />
            <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
        </SkeletonFooterBar>
    </SkeletonPageShell>
);

export const SettingsCardsPageSkeleton: FC = () => (
    <SkeletonPageShell aria-hidden='true'>
        <SkeletonPageHeader $margin='0 0 16px'>
            <SkeletonStack $gap='8px' $flex='1' $margin='16px 0'>
                <SkeletonBlock $width='min(260px, 100%)' $height='26px' />
            </SkeletonStack>
        </SkeletonPageHeader>
        <PageBlocksListSkeleton />
    </SkeletonPageShell>
);
